import Goal from "../goal"
import {Access} from "../../../gd/const/access"
import {GoalType} from "../const"
import GoalEvent from "../item/goal-event"
import GoalProject from "../item/goal-project"
import GoalTask from "../item/goal-task"

export default class GoalAccessControl {

    private goal : Goal

    constructor(goal:Goal) {
        this.goal = goal
    }

    get isGoalArchived() {
        return this.goal.rootGoal.isArchived
    }

    get isGoalUser() {
        return !!this.goal.users.get(gd.user.id)
    }

    get isGoalOwner() {
        return this.goal.ownerUserIds.includes(gd.user.id)
    }

    get isGoalAssignee() {
        return !!this.goal.assigneeUserIds?.includes(gd.user.id)
    }

    // this is a very default check
    get hasAccessAndGoalIsOpen():boolean {
        if (this.isGoalArchived) return false

        return this.hasAccess
    }

    get hasAccess():boolean {
        const user = this.goal.users.get(gd.user.id)
        switch (user?.access) {
            case Access.FULL    : return true

            case Access.STANDARD:
            case Access.LIMITED : return this.isGoalOwner || this.isGoalAssignee
        }

        if (this.isGoalUser) return true

        return false
    }

    get canSee():boolean {
        if (this.goal.isRoot && this.isGoalUser) return true

        const user = this.goal.users.get(gd.user.id)

        switch (user?.access) {
            case Access.FULL    :
            case Access.STANDARD: return true

            case Access.LIMITED : return this.isGoalOwner || this.isGoalAssignee
        }

        return false
    }

    get canChangeCover():boolean {
        return this.hasAccessAndGoalIsOpen
    }

    get canChangeDescription():boolean {
        return this.hasAccessAndGoalIsOpen
    }

    get canChangeUsers():boolean {
        return this.hasAccessAndGoalIsOpen
    }

    get canChangeOwners():boolean {
        /*const isAllowedByType = () => {
            switch (this.goal.type) {
                case GoalType.PROJECT   : return false  // no owners change for projects
                case GoalType.TASK      : return false  // no owners change for tasks
                case GoalType.EVENT     : return false  // no owners change for events
                default                 : return true   // all others are allowed to change owners
            }
        }*/
        return this.hasAccessAndGoalIsOpen /*&& isAllowedByType()*/
    }

    get canChangeAssignee():boolean {
        const isAllowedByType = () => {
            switch (this.goal.type) {
                case GoalType.PROJECT   : return true

                case GoalType.TASK      :
                    const goalTask = this.goal as GoalTask
                    return gd.ac.check.task.editAssignedToUser(goalTask.task.projectId, goalTask.task)

                case GoalType.EVENT     :
                    const goalEvent = this.goal as GoalEvent
                    return gd.ac.check.event.canEditEvent(goalEvent.event)      // event access check

                    /*return goalEvent.event.type === EventType.PROJECT_MILESTONE // assignee change is allowed for Milestones only
                        && gd.ac.check.event.canEditEvent(goalEvent.event)      // event access check*/

                default                 : return true   // all others are allowed to change assignee
            }
        }
        return this.hasAccessAndGoalIsOpen && isAllowedByType()
    }

    get canChangeTargetValue():boolean {
        return this.hasAccessAndGoalIsOpen
    }

    get canChangeType():boolean {
        return this.hasAccessAndGoalIsOpen
    }

    get canChangeIcon():boolean {
        return this.hasAccess
    }

    get canChangeTitle():boolean {
        return this.hasAccessAndGoalIsOpen
    }

    get canChangeProject():boolean {
        return this.hasAccessAndGoalIsOpen
    }

    get canChangeProgress():boolean {
        const isAllowedByType = () => {
            switch (this.goal.type) {
                case GoalType.PROJECT   :
                    const goalProject = this.goal as GoalProject
                    return gd.ac.check.project.standardAccess(goalProject.project.id)

                case GoalType.TASK      :
                    const goalTask = this.goal as GoalTask
                    return gd.ac.check.task.editStatusPriority(goalTask.task.projectId, goalTask.task)

                case GoalType.EVENT     :
                    const goalEvent = this.goal as GoalEvent
                    return gd.ac.check.event.canEditEvent(goalEvent.event)  // event access check

                default                 : return true   // all others are allowed to change progress
            }
        }

        return this.hasAccessAndGoalIsOpen && isAllowedByType()
    }

    get canChangeHealth():boolean {
        return this.hasAccessAndGoalIsOpen
    }

    get canChangeDates():boolean {
        const isAllowedByType = () => {
            switch (this.goal.type) {
                case GoalType.PROJECT   :
                    const goalProject = this.goal as GoalProject
                    return gd.ac.check.project.standardAccess(goalProject.project.id)

                case GoalType.TASK      :
                    const goalTask = this.goal as GoalTask
                    return gd.ac.check.task.editStartEnd(goalTask.task.projectId, goalTask.task)

                case GoalType.EVENT     :
                    const goalEvent = this.goal as GoalEvent
                    return gd.ac.check.event.canEditEvent(goalEvent.event)  // event access check

                default                 : return true   // all others are allowed to change dates
            }
        }

        return this.hasAccessAndGoalIsOpen && isAllowedByType()
    }

    get canAddComment():boolean {
        return this.hasAccessAndGoalIsOpen
    }

    get canAddSubGoals():boolean {
        return this.hasAccessAndGoalIsOpen
    }

    get canDuplicate():boolean {
        return this.hasAccess
    }

    get canRemove():boolean {
        return this.hasAccess
    }

    get canArchive():boolean {
        return this.hasAccess && this.goal.isRoot
    }
}
